import type {
  CreateProjectPayload,
  Paginated,
  Project,
  ProjectListItem,
  UpdateProjectPayload,
} from '@atlas/api-contract';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/errors';
import {
  createProject,
  deleteProject,
  getProject,
  listProjects,
  updateProject,
} from '@/lib/api/projects';
import { queryKeys } from '@/lib/query/keys';

import { projectsSearchCodec, type ProjectsSearch } from './projects-search';

/**
 * React Query bindings for `/v1/projects`.
 *
 * No mutation here is optimistic. Every one of them either creates something
 * with a server-assigned id or destroys a subtree, so the response is awaited
 * and only then is the cache invalidated — a rolled-back optimistic delete of a
 * project full of comments is a worse experience than a half-second wait.
 *
 * `onSuccess` returns the invalidation promise, which makes `mutateAsync`
 * settle only once the list has caught up. Dialogs stay open with a spinner for
 * that moment rather than closing onto stale rows.
 */

/** Query options for one page of the list, reusable from a route loader. */
export const projectsQueryOptions = (search: ProjectsSearch) => {
  const query = projectsSearchCodec.toQuery(search);

  return queryOptions<Paginated<ProjectListItem>, ApiError>({
    queryKey: queryKeys.projects.list(query),
    queryFn: ({ signal }) => listProjects(query, { signal }),
    /*
     * Paging and typing both change the key. Without this the grid would be
     * replaced by a skeleton on every keystroke; with it the previous page
     * stays put, dimmed, until the next one arrives.
     */
    placeholderData: keepPreviousData,
  });
};

export const useProjectsQuery = (
  search: ProjectsSearch,
): UseQueryResult<Paginated<ProjectListItem>, ApiError> =>
  useQuery(projectsQueryOptions(search));

/**
 * One project, by id.
 *
 * This is the only read that can tell a missing project from an empty one: the
 * prototypes list returns an empty page for an unknown `projectId` rather than
 * a 404, so the project page asks for the project itself and lets *this* query's
 * 404 decide whether to render a list or a "does not exist" (spec R6.6).
 */
export const projectQueryOptions = (projectId: string) =>
  queryOptions<Project, ApiError>({
    queryKey: queryKeys.projects.detail(projectId),
    queryFn: ({ signal }) => getProject(projectId, { signal }),
  });

export const useProjectQuery = (
  projectId: string,
): UseQueryResult<Project, ApiError> => useQuery(projectQueryOptions(projectId));

export const useCreateProject = (): UseMutationResult<
  Project,
  ApiError,
  CreateProjectPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<Project, ApiError, CreateProjectPayload>({
    mutationFn: createProject,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() }),
  });
};

export interface UpdateProjectVariables {
  projectId: string;
  payload: UpdateProjectPayload;
}

export const useUpdateProject = (): UseMutationResult<
  Project,
  ApiError,
  UpdateProjectVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<Project, ApiError, UpdateProjectVariables>({
    mutationFn: ({ projectId, payload }) => updateProject(projectId, payload),
    onSuccess: (project) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.detail(project.id),
        }),
      ]),
  });
};

export const useDeleteProject = (): UseMutationResult<void, ApiError, string> => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: deleteProject,
    /*
     * The delete cascades to prototypes, versions, threads and comments, so
     * anything cached below this project is now describing rows that no longer
     * exist. Invalidating the whole tree is the only correct answer.
     */
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.all() }),
  });
};
